import { CATEGORY_LABELS, CATEGORY_STORIES } from '@/constants/products'
import { clamp01, lerp, smoothstep01 } from '@/lib/utils/math'
import { productCategorySchema } from '@/schemas/product.schema'

type CardSide = 'left' | 'right'

const SECTION_TITLE = 'O jardim de categorias'
const HEADING_TEXT = 'Um jardim para cada sentimento'
const PAIR_STAGGER = 0.15
const PAIR_FIRST_START = 0.04
const PAIR_DURATION = 0.28
const ENTER_OFFSET_PX = 66
const EXIT_START = 0.88

const GARDEN_CARDS = productCategorySchema.options.map((category, index) => ({
  category,
  label: CATEGORY_LABELS[category],
  story: CATEGORY_STORIES[category],
  side: (index % 2 === 0 ? 'left' : 'right') as CardSide,
  windowStart: PAIR_FIRST_START + Math.floor(index / 2) * PAIR_STAGGER,
}))

export interface GardenSectionModelProps {
  progress: number
}

function resolveCardMotion(
  progress: number,
  windowStart: number,
  side: CardSide,
): { opacity: number; offsetX: number } {
  const enter = smoothstep01((progress - windowStart) / PAIR_DURATION)
  const exit = smoothstep01((progress - EXIT_START) / (1 - EXIT_START))
  const edgeSign = side === 'left' ? -1 : 1
  return {
    opacity: clamp01(enter * (1 - exit)),
    offsetX: lerp(ENTER_OFFSET_PX * edgeSign, 0, enter),
  }
}

export function useGardenSectionModel({ progress }: GardenSectionModelProps) {
  const cards = GARDEN_CARDS.map((card) => {
    const motion = resolveCardMotion(progress, card.windowStart, card.side)
    const opacity = Math.round(motion.opacity * 1000) / 1000
    return {
      category: card.category,
      label: card.label,
      story: card.story,
      side: card.side,
      style: {
        opacity,
        transform: `translate3d(${motion.offsetX.toFixed(2)}px, 0, 0)`,
        pointerEvents: opacity > 0.6 ? ('auto' as const) : ('none' as const),
      },
    }
  })

  const stageOpacity = clamp01(
    Math.min(smoothstep01(progress / 0.1), smoothstep01((1 - progress) / 0.12)),
  )

  return {
    sectionTitle: SECTION_TITLE,
    headingText: HEADING_TEXT,
    categories: GARDEN_CARDS.map((card) => ({
      category: card.category,
      label: card.label,
    })),
    cards,
    stageInert: progress <= 0 || progress >= 1,
    stageStyle: { opacity: Math.round(stageOpacity * 1000) / 1000 },
  }
}
